import { useState, useCallback, useEffect, useRef } from 'react';
import Konva from 'konva';
import { Camera, screenToWorld, worldToScreen, Point } from '../utils/cameraUtils';

const SCALE_BY = 1.05;
const MIN_SCALE = 0.1;
const MAX_SCALE = 5;
const INITIAL_SCALE = 0.4;

/**
 * Custom hook to manage panning and zooming of a Konva stage using a camera model.
 * Implements Figma-like controls: scroll to pan, Cmd/Ctrl+scroll to zoom.
 * @param stageRef - Ref to the Konva.Stage object.
 * @param templateWidth - Width of the template to center
 * @param templateHeight - Height of the template to center
 */
export const useStagePanAndZoom = (
  stageRef: React.RefObject<Konva.Stage>,
  templateWidth: number,
  templateHeight: number,
) => {
  const [camera, setCamera] = useState<Camera>({ scale: INITIAL_SCALE, position: { x: 0, y: 0 } });
  const [isPanning, setIsPanning] = useState(false);
  const lastPointerPos = useRef<Point | null>(null);

  // Center the template initially and when its dimensions change.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    // Use a timeout to ensure the stage has its final dimensions after layout.
    const timeoutId = setTimeout(() => {
      const stageWidth = stage.width();
      const stageHeight = stage.height();

      if (stageWidth > 0 && stageHeight > 0) {
        const scaledTemplateWidth = templateWidth * INITIAL_SCALE;
        const scaledTemplateHeight = templateHeight * INITIAL_SCALE;

        const centerX = (stageWidth - scaledTemplateWidth) / 2;
        const centerY = (stageHeight - scaledTemplateHeight) / 2;

        setCamera({
          scale: INITIAL_SCALE,
          position: { x: centerX, y: centerY },
        });
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [stageRef, templateWidth, templateHeight]);

  const handleWheel = useCallback((e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    // Zoom with Cmd/Ctrl + scroll (like Figma)
    if (e.evt.ctrlKey || e.evt.metaKey) {
      setCamera(currentCamera => {
        const worldPoint = screenToWorld(pointer, currentCamera);
        const newScale = e.evt.deltaY > 0 
          ? currentCamera.scale / SCALE_BY 
          : currentCamera.scale * SCALE_BY;
        const clampedScale = Math.max(MIN_SCALE, Math.min(newScale, MAX_SCALE));
        const newCamera: Camera = {
          scale: clampedScale,
          position: { x: 0, y: 0 } // Temp position
        };
        const newScreenPoint = worldToScreen(worldPoint, newCamera);
        newCamera.position = {
          x: pointer.x - newScreenPoint.x,
          y: pointer.y - newScreenPoint.y,
        };
        return newCamera;
      });
    } else {
      // Pan with regular scroll (like Figma two-finger gesture)
      setCamera(prevCamera => ({
        ...prevCamera,
        position: {
          x: prevCamera.position.x - e.evt.deltaX,
          y: prevCamera.position.y - e.evt.deltaY,
        }
      }));
    }
  }, [stageRef]);

  const handleMouseDown = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    // Middle mouse button for panning
    if (e.evt.button === 1) {
      setIsPanning(true);
      lastPointerPos.current = stageRef.current?.getPointerPosition() || null;
      
      const stage = stageRef.current;
      const container = stage?.container();
      if (container) {
        container.style.cursor = 'grabbing';
      }
    }
  }, [stageRef]);

  const handleMouseMove = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!isPanning) return;
    
    const stage = stageRef.current;
    if (!stage) return;

    const newPointerPos = stage.getPointerPosition();
    if (lastPointerPos.current && newPointerPos) {
      const dx = newPointerPos.x - lastPointerPos.current.x;
      const dy = newPointerPos.y - lastPointerPos.current.y;
      
      setCamera(prevCamera => ({
        ...prevCamera,
        position: {
          x: prevCamera.position.x + dx,
          y: prevCamera.position.y + dy,
        }
      }));
      
      lastPointerPos.current = newPointerPos;
    }
  }, [isPanning, stageRef]);

  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      lastPointerPos.current = null;
      
      const stage = stageRef.current;
      const container = stage?.container();
      if (container) {
        container.style.cursor = 'default';
      }
    }
  }, [isPanning, stageRef]);

  // Setup mouse event listeners for panning
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    stage.on('mousemove', handleMouseMove);
    stage.on('mouseup', handleMouseUp);
    stage.on('mouseleave', handleMouseUp);

    return () => {
      stage.off('mousemove', handleMouseMove);
      stage.off('mouseup', handleMouseUp);
      stage.off('mouseleave', handleMouseUp);
    };
  }, [stageRef, handleMouseMove, handleMouseUp]);

  return {
    camera,
    handleWheel,
    handleMouseDown,
  };
};